import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { FileText, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { usePets } from '@/contexts/pets';
import logger from '@/utils/logger';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useDocumentUpload } from '@/components/documents/form/hooks/useDocumentUpload';

interface UploadDocumentStepProps {
  petId: string | null;
  onDocumentUploaded: () => void;
}

/**
 * Second step of the onboarding tour - Upload a document
 */
const UploadDocumentStep: React.FC<UploadDocumentStepProps> = ({ 
  petId, 
  onDocumentUploaded 
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [documentName, setDocumentName] = useState('');
  const [category, setCategory] = useState('');
  const [selectedPetId, setSelectedPetId] = useState<string | undefined>(petId || undefined);
  
  const { uploadDocument } = useDocumentUpload();
  const { user } = useAuth();
  const { pets } = usePets();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      
      // Check file size (limit to 10MB)
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error('File is too large. Please select a file under 10MB.');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      
      setFile(selectedFile);
      
      // Set a default document name based on the file name
      const baseName = selectedFile.name.split('.')[0] || 'Untitled Document';
      setDocumentName(baseName);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file to upload.');
      return;
    }

    if (!documentName) {
      toast.error('Please provide a document name.');
      return;
    }

    if (!category) {
      toast.error('Please select a document category.');
      return;
    }

    try {
      setIsUploading(true);
      toast.loading('Uploading document...', { id: 'onboarding-upload' });
      
      // Use the actual document upload function from the useDocumentUpload hook
      const result = await uploadDocument({
        file,
        documentName,
        category,
        petId: selectedPetId
      });
      
      toast.dismiss('onboarding-upload');
      
      if (result) {
        logger.info('Document uploaded successfully during onboarding');
        toast.success('Document uploaded successfully!');
        onDocumentUploaded();
      } else {
        throw new Error('Upload failed - no result returned');
      }
    } catch (error) {
      toast.dismiss('onboarding-upload');
      logger.error('Error uploading document during onboarding:', error);
      toast.error('Failed to upload document. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Document categories based on the screenshot
  const documentCategories = [
    'Vaccination Record',
    'Medical Report',
    'Insurance Policy',
    'Adoption Certificate',
    'Training Certificate',
    'Microchip Information',
    'Prescription',
    'Other'
  ];

  const renderFileUploadSection = () => {
    if (file) {
      return (
        <div className="p-4 border rounded-lg flex items-center space-x-3 mb-4">
          <FileText className="h-6 w-6 text-gray-500" />
          <div className="flex-1 truncate">{file.name}</div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setFile(null)}
            disabled={isUploading}
          >
            Change
          </Button>
        </div>
      );
    }
    
    return (
      <div 
        className="border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors mb-4"
        onClick={triggerFileInput}
      >
        <Upload className="h-8 w-8 text-gray-400 mb-2" />
        <p className="text-sm text-gray-600 mb-1">Click to upload or drag and drop</p>
        <p className="text-xs text-gray-500">PDF, JPG, PNG (max. 10MB)</p>
      </div>
    );
  };

  return (
    <div className="flex flex-col items-center">
      {/* Header section - responsive for mobile/desktop */}
      <div className="sticky top-0 bg-white z-10 sm:pb-4 pb-2 w-full flex flex-col items-center sm:space-y-4 space-y-2">
        <h2 className="sm:text-2xl text-xl font-bold text-center sm:pt-0 pt-1">Upload a Document</h2>
        
        <p className="text-center text-gray-600 sm:text-base text-sm sm:px-0 px-4 sm:leading-normal leading-tight">
          Great! Now let's upload a document—like vaccination records or adoption papers—for your pet.
        </p>

        <div className="sm:w-20 sm:h-20 w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center">
          <FileText className="sm:h-10 sm:w-10 h-8 w-8 text-violet-500" />
        </div>
      </div>

      {/* Content section - scrollable only on mobile */}
      <div className="w-full sm:overflow-visible sm:max-h-none max-sm:overflow-y-auto max-sm:max-h-[370px] sm:py-4 py-2">
        <div className="w-full sm:space-y-4 space-y-3">
          {/* Document Name Field */}
          <div className="space-y-2">
            <Label htmlFor="documentName">Document Name</Label>
            <Input
              id="documentName"
              placeholder="e.g. Vaccination Certificate"
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              disabled={isUploading}
            />
          </div>
          
          {/* Category Dropdown */}
          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Select 
              value={category} 
              onValueChange={setCategory}
              disabled={isUploading}
            >
              <SelectTrigger id="category">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {documentCategories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Pet Selector */}
          <div className="space-y-2">
            <Label htmlFor="pet-select">Assign to Pet</Label>
            <Select 
              value={selectedPetId || "none"} 
              onValueChange={(value) => setSelectedPetId(value === "none" ? undefined : value)}
              disabled={isUploading}
            >
              <SelectTrigger id="pet-select">
                <SelectValue placeholder="Select a pet" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">General (No pet)</SelectItem>
                {pets.map(pet => (
                  <SelectItem key={pet.id} value={pet.id}>
                    {pet.name} {pet.breed ? `(${pet.breed})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Document File Upload */}
          <div className="space-y-2">
            <Label>Document File</Label>
            {renderFileUploadSection()}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*, application/pdf"
              className="hidden"
              aria-label="Upload document"
            />
          </div>
          
          {/* Action buttons - responsive for mobile/desktop */}
          <div className="flex flex-col sm:space-y-3 space-y-2 w-full sm:mb-0 mb-4">
            <Button 
              onClick={handleUpload}
              disabled={isUploading || !file || !documentName || !category}
              className="w-full sm:text-base text-sm sm:py-2 py-1.5"
            >
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 sm:h-4 sm:w-4 h-3 w-3 animate-spin" />
                  Uploading...
                </>
              ) : 'Continue'}
            </Button>
            
            <Button 
              variant="ghost" 
              onClick={onDocumentUploaded} 
              disabled={isUploading} 
              className="text-gray-500 sm:text-base text-sm sm:py-2 py-1.5"
            >
              Skip this step
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadDocumentStep;